import React, { useContext, useMemo } from 'react';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { isPayrollFieldEditableOnTimesheet } from 'shared/utils/timesheet';
import { PayrollFieldTotalAggregateRenderer, TimesheetPayrollFieldRenderer } from 'timeSheet/components/CellRenderers';
import { getLeafChildrenData } from 'timeSheet/utils';

const getFieldValue = (row, fieldId) =>
  Number((row?.timesheetPayrollFields?.fields || []).find((payrollField) => payrollField?.id === fieldId)?.value) || 0;

const sumFieldValues = (rows, fieldId) => (rows || []).reduce((total, row) => total + getFieldValue(row, fieldId), 0);

const useTimesheetPayrollFieldsColumns = ({ payrollFields = [], updatePayrollField } = {}) => {
  const { t } = useContext(ConfigContext);

  return useMemo(() => {
    if (!updatePayrollField || payrollFields.length === 0) {
      return [];
    }

    return payrollFields.map(({ id, slug, isGeneric, type }) => ({
      colId: `timesheetPayrollField.${id}`,
      headerName: isGeneric ? t(slug) : slug,
      width: 100,
      sortable: false,
      cellClass: 'justify-start',
      valueGetter: (params) => {
        if (params.node?.group) {
          return sumFieldValues(getLeafChildrenData(params.node), id);
        }

        if (params.data?.summarized) {
          return params.data?.timesheetPayrollFieldsTotal?.[id] || 0;
        }

        return `${getFieldValue(params.data, id)}|${isPayrollFieldEditableOnTimesheet(params.data)}`;
      },
      cellRenderer: (params) => {
        if (params.node?.group) {
          return PayrollFieldTotalAggregateRenderer({
            rowData: { subRows: true, fieldsTotal: { [id]: sumFieldValues(getLeafChildrenData(params.node), id) } },
            columnData: { fieldId: id, type }
          });
        }

        if (params.data?.summarized) {
          return PayrollFieldTotalAggregateRenderer({
            rowData: { summarized: true, fieldsTotal: params.data.timesheetPayrollFieldsTotal },
            columnData: { fieldId: id, type }
          });
        }

        return (
          <TimesheetPayrollFieldRenderer
            rowData={params.data}
            columnData={{ fieldId: id, updateField: updatePayrollField }}
          />
        );
      }
    }));
  }, [payrollFields, updatePayrollField, t]);
};

export default useTimesheetPayrollFieldsColumns;
